import { combineQueries, createEntityQuery, Order } from '@datorama/akita';
import { formatISO } from 'date-fns';
import { isEqual } from 'lodash-es';
import { distinctUntilChanged, map } from 'rxjs';
import store from './store';
import { reducedContext$ as activeOrganisation$ } from 'context/OrganisationContext/query';
import { context$ as periodContext$ } from 'context/PeriodContext/query';
import { type KPI, type KpiArea, type KpiContent, type KpiField, type KpiScope, type KpiSub, type Organisation, type Period } from 'types/models';

const hasEveryScopeMateriality = (
   scopes: KpiScope[],
   from: string,
   to: string,
   organisationId?: Organisation['id'],
   isMaterial?: boolean,
   filterScopes = false
) =>
   scopes
      .filter((kpiScope) => kpiScope.from === from && kpiScope.to === to && (filterScopes ? kpiScope.organisationId === organisationId : true))
      .every((kpiScope) => !!kpiScope.isMaterial === isMaterial);

const hasEveryScopeMaterialityNested = (
   node: any,
   from: string,
   to: string,
   organisationId?: Organisation['id'],
   isMaterial?: boolean,
   defaultIsMaterial = true,
   filterScopes = false
) => {
   const childKeys = ['kpis', 'kpiSubs', 'kpiContents', 'kpiFields'];
   const relevantScopes = (node?.kpiScopes ?? []).filter(({ organisationId: relevantScopeOrganisationId }: KpiScope) =>
      filterScopes ? organisationId === relevantScopeOrganisationId : true
   );
   if (relevantScopes.length === 0) {
      return defaultIsMaterial === isMaterial;
   }

   if (childKeys.some((childKey) => childKey in node)) {
      const childKey = childKeys.find((childKey_) => childKey_ in node);

      return (
         hasEveryScopeMateriality(relevantScopes, from, to, organisationId, isMaterial) &&
         childKey &&
         node[childKey]
            .filter((childNode: KpiField) => (childKey === 'kpiFields' ? !childNode.fieldGroupId : true))
            .every((childNode: any) => hasEveryScopeMaterialityNested(childNode, from, to, organisationId, isMaterial, isMaterial))
      );
   }

   return hasEveryScopeMateriality(relevantScopes, from, to, organisationId, isMaterial);
};

const hasAnyChildScope = (node: any, from: string, to: string, organisationId?: Organisation['id'], filterScopes = false) => {
   const childKeys = ['kpis', 'kpiSubs', 'kpiContents', 'kpiFields'];

   const relevantScopes = (node?.kpiScopes ?? []).filter(({ organisationId: relevantScopeOrganisationId }: KpiScope) =>
      filterScopes ? organisationId === relevantScopeOrganisationId : true
   );

   if (childKeys.some((childKey) => childKey in node)) {
      const childKey = childKeys.find((childKey_) => childKey_ in node);

      return (
         relevantScopes.length > 0 ||
         (childKey &&
            node[childKey]
               .filter((childNode: KpiField) => (childKey === 'kpiFields' ? !childNode.fieldGroupId : true))
               .some((childNode: any) => hasAnyChildScope(childNode, from, to, organisationId, filterScopes)))
      );
   }

   return relevantScopes.length > 0;
};

const hasAnyScope = (
   from: string,
   to: string,
   organisationId?: Organisation['id'],
   nodes: KpiArea[] | KPI[] | KpiSub[] | KpiContent[] | KpiField[] = [],
   filterScopes = false
) => {
   return nodes.some((node) => hasAnyChildScope(node, from, to, organisationId, filterScopes));
};

export const calculateScope = (
   from: string,
   to: string,
   organisation: Organisation,
   currentScope = true,
   nodes: KpiArea[] | KPI[] | KpiSub[] | KpiContent[] | KpiField[] = [],
   filterScopes = false
) => {
   if (!hasAnyScope(from, to, organisation.id, nodes, filterScopes)) {
      return currentScope;
   }

   if (nodes.every((node) => hasEveryScopeMaterialityNested(node, from, to, organisation.id, true, true, filterScopes))) {
      return currentScope !== true ? null : true;
   }
   if (
      nodes.every((node) => hasEveryScopeMaterialityNested(node, from, to, organisation.id, false, false, filterScopes)) &&
      !organisation?.allowMaterialityAssessment
   ) {
      return currentScope !== false ? null : false;
   }

   return currentScope === false ? currentScope : null;
};

export const query = createEntityQuery(store, {
   sortBy: 'position',
   sortByOrder: Order.ASC,
});

export const entities$ = query.selectAll();

function findIsMaterial(periodContext: Period, kpiScopes: KpiScope[] = [], activeOrganisation: Organisation) {
   const { from: periodFrom, to: periodTo } = periodContext;
   const relevantScopes = kpiScopes.filter(({ from, to }) => from === periodFrom && to === periodTo);
   if (relevantScopes.some(({ isMaterial }) => isMaterial)) {
      return relevantScopes.find(({ isMaterial }) => isMaterial)?.isMaterial ?? undefined;
   } else {
      return (
         relevantScopes.find(
            ({ isMaterial, organisationId }) =>
               !(isMaterial === false && organisationId !== activeOrganisation.id && activeOrganisation.allowMaterialityAssessment)
         )?.isMaterial ?? undefined
      );
   }
}

const scopedEntities$ = combineQueries([entities$, periodContext$, activeOrganisation$]).pipe(
   map(([kpiSet, periodContext, activeOrganisation]) =>
      Array.isArray(kpiSet) && periodContext && activeOrganisation
         ? kpiSet.map((kpiArea) => ({
              ...kpiArea,
              type: 'kpiArea',
              scope: calculateScope(
                 periodContext?.from,
                 periodContext?.to,
                 activeOrganisation,
                 findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation),
                 kpiArea.kpis
              ),
              kpis: (kpiArea?.kpis ?? []).map((kpi: KPI) => ({
                 ...kpi,
                 type: 'kpi',
                 scope: calculateScope(
                    periodContext?.from,
                    periodContext?.to,
                    activeOrganisation,
                    findIsMaterial(periodContext, kpi?.kpiScopes, activeOrganisation),
                    kpi.kpiSubs
                 ),
                 kpiSubs: (kpi?.kpiSubs ?? []).map((kpiSub: KpiSub) => ({
                    ...kpiSub,
                    type: 'kpiSub',
                    scope: calculateScope(
                       periodContext?.from,
                       periodContext?.to,
                       activeOrganisation,
                       findIsMaterial(periodContext, kpiSub?.kpiScopes, activeOrganisation),
                       kpiSub.kpiContents
                    ),
                    kpiContents: (kpiSub?.kpiContents ?? []).map((kpiContent: KpiContent) => ({
                       ...kpiContent,
                       type: 'kpiContent',
                       scope: calculateScope(
                          periodContext?.from,
                          periodContext?.to,
                          activeOrganisation,
                          findIsMaterial(periodContext, kpiContent?.kpiScopes, activeOrganisation),
                          kpiContent.kpiFields.filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                       ),
                       kpiFields: (kpiContent?.kpiFields ?? [])
                          .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                          .map((kpiField: KpiField) => ({
                             ...kpiField,
                             type: 'kpiField',
                             scope: calculateScope(
                                periodContext?.from,
                                periodContext?.to,
                                activeOrganisation,
                                findIsMaterial(periodContext, kpiField?.kpiScopes, activeOrganisation)
                             ),
                          })),
                    })),
                 })),
              })),
           }))
         : []
   ),
   distinctUntilChanged(isEqual)
);

export const scopedEntitiesResult$ = combineQueries([entities$, periodContext$, activeOrganisation$]).pipe(
   map(([kpiSet, periodContext, activeOrganisation]) =>
      Array.isArray(kpiSet) && periodContext && activeOrganisation
         ? (kpiSet ?? []).map((kpiArea) => ({
              ...kpiArea,
              type: 'kpiArea',
              path: [kpiArea.slug],
              scope: calculateScope(
                 periodContext?.from,
                 periodContext?.to,
                 activeOrganisation,
                 findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation),
                 kpiArea.kpis
              ),
              kpis: (kpiArea?.kpis ?? []).map((kpi: KPI) => ({
                 ...kpi,
                 type: 'kpi',
                 path: [kpiArea.slug, kpi.slug],
                 scope: calculateScope(
                    periodContext?.from,
                    periodContext?.to,
                    activeOrganisation,
                    findIsMaterial(periodContext, kpi?.kpiScopes, activeOrganisation) ??
                       findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation),
                    kpi.kpiSubs
                 ),
                 kpiSubs: (kpi?.kpiSubs ?? []).map((kpiSub: KpiSub) => ({
                    ...kpiSub,
                    type: 'kpiSub',
                    path: [kpiArea.slug, kpi.slug, kpiSub.slug],
                    scope: calculateScope(
                       periodContext?.from,
                       periodContext?.to,
                       activeOrganisation,
                       findIsMaterial(periodContext, kpiSub?.kpiScopes, activeOrganisation) ??
                          findIsMaterial(periodContext, kpi?.kpiScopes, activeOrganisation) ??
                          findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation),
                       kpiSub.kpiContents
                    ),
                    kpiContents: (kpiSub?.kpiContents ?? []).map((kpiContent: KpiContent) => ({
                       ...kpiContent,
                       type: 'kpiContent',
                       path: [kpiArea.slug, kpi.slug, kpiSub.slug, kpiContent.slug],
                       scope: calculateScope(
                          periodContext?.from,
                          periodContext?.to,
                          activeOrganisation,
                          findIsMaterial(periodContext, kpiContent?.kpiScopes, activeOrganisation) ??
                             findIsMaterial(periodContext, kpiSub?.kpiScopes, activeOrganisation) ??
                             findIsMaterial(periodContext, kpi?.kpiScopes, activeOrganisation) ??
                             findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation),
                          kpiContent.kpiFields.filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                       ),
                       kpiFields: (kpiContent?.kpiFields ?? [])
                          .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                          .map((kpiField: KpiField) => ({
                             ...kpiField,
                             type: 'kpiField',
                             path: [kpiArea.slug, kpi.slug, kpiSub.slug, kpiContent.slug, kpiField.slug],
                             scope: calculateScope(
                                periodContext?.from,
                                periodContext?.to,
                                activeOrganisation,
                                findIsMaterial(periodContext, kpiField?.kpiScopes, activeOrganisation) ??
                                   findIsMaterial(periodContext, kpiContent?.kpiScopes, activeOrganisation) ??
                                   findIsMaterial(periodContext, kpiSub?.kpiScopes, activeOrganisation) ??
                                   findIsMaterial(periodContext, kpi?.kpiScopes, activeOrganisation) ??
                                   findIsMaterial(periodContext, kpiArea?.kpiScopes, activeOrganisation)
                             ),
                          })),
                    })),
                 })),
              })),
           }))
         : []
   ),
   distinctUntilChanged(isEqual)
);

export const flatScopedEntitiesResult$ = scopedEntitiesResult$.pipe(
   map((kpiAreas) => [
      ...kpiAreas,
      ...kpiAreas.flatMap((kpiArea: KpiArea) => kpiArea.kpis),
      ...kpiAreas.flatMap((kpiArea: KpiArea) => (kpiArea?.kpis ?? []).flatMap((kpi: KPI) => kpi.kpiSubs)),
      ...kpiAreas.flatMap((kpiArea: KpiArea) =>
         (kpiArea?.kpis ?? []).flatMap((kpi: KPI) => kpi.kpiSubs.flatMap((kpiSub: KpiSub) => kpiSub.kpiContents))
      ),
      ...kpiAreas.flatMap((kpiArea: KpiArea) =>
         (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
            (kpi?.kpiSubs ?? []).flatMap((kpiSub: KpiSub) =>
               (kpiSub?.kpiContents ?? []).flatMap((kpiContent: KpiContent) =>
                  (kpiContent?.kpiFields ?? []).filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
               )
            )
         )
      ),
   ]),
   distinctUntilChanged(isEqual)
);

export const flatEntitiesInScope$ = scopedEntities$.pipe(
   map((kpiAreas) => [
      ...kpiAreas.filter((kpiArea: KpiArea) => kpiArea.scope === true),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) => kpiArea.kpis.filter((kpi: KPI) => kpi.scope === true)),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            kpiArea.kpis.filter((kpi: KPI) => kpi.scope === null).flatMap((kpi: KPI) => kpi.kpiSubs.filter((kpiSub: KpiSub) => kpiSub.scope === true))
         ),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            kpiArea.kpis
               .filter((kpi: KPI) => kpi.scope === null)
               .flatMap((kpi: KPI) =>
                  kpi.kpiSubs
                     .filter((kpiSub: KpiSub) => kpiSub.scope === null)
                     .flatMap((kpiSub: KpiSub) => kpiSub.kpiContents.filter((kpiContent: KpiContent) => kpiContent.scope === true))
               )
         ),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            kpiArea.kpis
               .filter((kpi: KPI) => kpi.scope === null)
               .flatMap((kpi: KPI) =>
                  kpi.kpiSubs
                     .filter((kpiSub: KpiSub) => kpiSub.scope === null)
                     .flatMap((kpiSub: KpiSub) =>
                        kpiSub.kpiContents
                           .filter((kpiContent: KpiContent) => kpiContent.scope === null)
                           .flatMap((kpiContent: KpiContent) =>
                              kpiContent.kpiFields
                                 .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                                 .filter((kpiField: KpiField) => kpiField.scope === true)
                           )
                     )
               )
         ),
   ]),
   distinctUntilChanged(isEqual)
);

export const flatEntitiesNotInScope$ = scopedEntities$.pipe(
   map((kpiAreas) => [
      ...kpiAreas.filter((kpiArea: KpiArea) => kpiArea.scope === false),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) => (kpiArea?.kpis ?? []).filter((kpi: KPI) => kpi.scope === false)),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            (kpiArea?.kpis ?? [])
               .filter((kpi: KPI) => kpi.scope === null)
               .flatMap((kpi: KPI) => (kpi?.kpiSubs ?? []).filter((kpiSub: KpiSub) => kpiSub.scope === false))
         ),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            (kpiArea?.kpis ?? [])
               .filter((kpi: KPI) => kpi.scope === null)
               .flatMap((kpi: KPI) =>
                  (kpi?.kpiSubs ?? [])
                     .filter((kpiSub: KpiSub) => kpiSub.scope === null)
                     .flatMap((kpiSub: KpiSub) => (kpiSub?.kpiContents ?? []).filter((kpiContent: KpiContent) => kpiContent.scope === false))
               )
         ),
      ...kpiAreas
         .filter((kpiArea: KpiArea) => kpiArea.scope === null)
         .flatMap((kpiArea: KpiArea) =>
            (kpiArea?.kpis ?? [])
               .filter((kpi: KPI) => kpi.scope === null)
               .flatMap((kpi: KPI) =>
                  (kpi?.kpiSubs ?? [])
                     .filter((kpiSub: KpiSub) => kpiSub.scope === null)
                     .flatMap((kpiSub: KpiSub) =>
                        (kpiSub?.kpiContents ?? [])
                           .filter((kpiContent: KpiContent) => kpiContent.scope === null)
                           .flatMap((kpiContent: KpiContent) =>
                              (kpiContent?.kpiFields ?? [])
                                 .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                                 .filter((kpiField: KpiField) => kpiField.scope === false)
                           )
                     )
               )
         ),
   ]),
   distinctUntilChanged(isEqual)
);

export const activeNode$ = combineQueries([scopedEntitiesResult$, query.select('activeNode')]).pipe(
   map(([kpiSet, activeNode]) => {
      let match: any;

      if (Array.isArray(kpiSet) && activeNode) {
         if (kpiSet.find((kpiArea) => activeNode?.type === 'kpiArea' && kpiArea.id === activeNode?.id)) {
            return kpiSet.find((kpiArea) => activeNode?.type === 'kpiArea' && kpiArea.id === activeNode?.id);
         }

         if (!match) {
            kpiSet.forEach((kpiArea) => {
               if (kpiArea.kpis.find((kpi: KPI) => activeNode?.type === 'kpi' && kpi.id === activeNode?.id)) {
                  match = kpiArea.kpis.find((kpi: KPI) => activeNode?.type === 'kpi' && kpi.id === activeNode?.id);
               }

               if (!match) {
                  kpiArea.kpis.forEach((kpi: KPI) => {
                     if (kpi.kpiSubs.find((kpiSub: KpiSub) => activeNode?.type === 'kpiSub' && kpiSub.id === activeNode?.id)) {
                        match = kpi.kpiSubs.find((kpiSub: KpiSub) => activeNode?.type === 'kpiSub' && kpiSub.id === activeNode?.id);
                     }

                     if (!match) {
                        kpi.kpiSubs.forEach((kpiSub: KpiSub) => {
                           if (
                              kpiSub.kpiContents.find(
                                 (kpiContent: KpiContent) => activeNode?.type === 'kpiContent' && kpiContent.id === activeNode?.id
                              )
                           ) {
                              match = kpiSub.kpiContents.find(
                                 (kpiContent: KpiContent) => activeNode?.type === 'kpiContent' && kpiContent.id === activeNode?.id
                              );
                           }

                           if (!match) {
                              kpiSub.kpiContents.forEach((kpiContent: KpiContent) => {
                                 if (
                                    kpiContent.kpiFields
                                       .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                                       .find((kpiField: KpiField) => activeNode?.type === 'kpiField' && kpiField.id === activeNode?.id)
                                 ) {
                                    match = kpiContent.kpiFields.find(
                                       (kpiField: KpiField) => activeNode?.type === 'kpiField' && kpiField.id === activeNode?.id
                                    );
                                 }
                              });
                           }
                        });
                     }
                  });
               }
            });
         }
      }

      return match;
   }),
   distinctUntilChanged(isEqual)
);

const someScopesMatch = (nodes: any[], isMaterial: boolean, from: string, to: string, organisationId?: Organisation['id']) =>
   nodes.every((node) =>
      node.kpiScopes.some(
         (kpiScope: KpiScope) =>
            kpiScope.from == from &&
            kpiScope.to == to &&
            kpiScope.reason &&
            kpiScope.isMaterial === isMaterial &&
            kpiScope.organisationId === organisationId &&
            Number.isInteger(kpiScope.businessImpact) &&
            Number.isInteger(kpiScope.stakeholderImpact)
      )
   ) || nodes.length === 0;

export const allInScopeAnswered$ = combineQueries([flatEntitiesInScope$, periodContext$, activeOrganisation$]).pipe(
   map(([nodes, periodContext, activeOrganisation]) =>
      Array.isArray(nodes) && periodContext && activeOrganisation
         ? someScopesMatch(
              nodes.filter(({ reportingStandard }) => reportingStandard?.key === 'GRI'),
              true,
              periodContext?.from,
              periodContext?.to,
              activeOrganisation.id
           )
         : false
   ),
   distinctUntilChanged(isEqual)
);

export const allOutOfScopeAnswered$ = combineQueries([flatEntitiesNotInScope$, periodContext$, activeOrganisation$]).pipe(
   map(([nodes, periodContext, activeOrganisation]) =>
      Array.isArray(nodes) && periodContext && activeOrganisation
         ? someScopesMatch(
              nodes.filter(({ reportingStandard }) => reportingStandard?.key === 'GRI'),
              false,
              periodContext?.from,
              periodContext?.to,
              activeOrganisation.id
           )
         : false
   ),
   distinctUntilChanged(isEqual)
);

export const statistics$ = query.select('statistics');

export const kpiScopes$ = combineQueries([entities$, periodContext$, activeOrganisation$]).pipe(
   map(([kpiAreas, periodContext, activeOrganisation]) =>
      Array.isArray(kpiAreas) && periodContext && activeOrganisation
         ? [
              ...kpiAreas.flatMap((kpiArea) =>
                 kpiArea?.kpiScopes?.length === 0
                    ? [
                         {
                            id: -1,
                            createdAt: formatISO(new Date()),
                            updateAt: formatISO(new Date()),
                            reason: '',
                            businessImpact: undefined,
                            stakeholderImpact: undefined,
                            isMaterial: true,
                            from: periodContext?.from,
                            to: periodContext?.to,
                            organisationId: activeOrganisation.id,
                            kind: 'kpiArea',
                            objectId: kpiArea.id,
                         } as Partial<KpiScope>,
                      ]
                    : (kpiArea?.kpiScopes ?? []).map((kpiScope: KpiScope) => ({ ...kpiScope, kind: 'kpiArea', objectId: kpiArea.id }))
              ),
              ...kpiAreas.flatMap((kpiArea) =>
                 (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                    (kpi?.kpiScopes ?? []).length === 0
                       ? [
                            {
                               id: -1,
                               createdAt: formatISO(new Date()),
                               updateAt: formatISO(new Date()),
                               reason: '',
                               businessImpact: undefined,
                               stakeholderImpact: undefined,
                               isMaterial: true,
                               from: periodContext?.from,
                               to: periodContext?.to,
                               organisationId: activeOrganisation.id,
                               kind: 'kpi',
                               objectId: kpi.id,
                            } as Partial<KpiScope>,
                         ]
                       : (kpi?.kpiScopes ?? []).map((kpiScope: KpiScope) => ({ ...kpiScope, kind: 'kpi', objectId: kpi.id }))
                 )
              ),
              ...kpiAreas.flatMap((kpiArea) =>
                 (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                    (kpi?.kpiSubs ?? []).flatMap((kpiSub: KpiSub) =>
                       (kpiSub?.kpiScopes ?? []).length === 0
                          ? [
                               {
                                  id: -1,
                                  createdAt: formatISO(new Date()),
                                  updateAt: formatISO(new Date()),
                                  reason: '',
                                  businessImpact: undefined,
                                  stakeholderImpact: undefined,
                                  isMaterial: true,
                                  from: periodContext?.from,
                                  to: periodContext?.to,
                                  organisationId: activeOrganisation.id,
                                  kind: 'kpiSub',
                                  objectId: kpiSub.id,
                               } as Partial<KpiScope>,
                            ]
                          : (kpiSub?.kpiScopes ?? []).map((kpiScope: KpiScope) => ({ ...kpiScope, kind: 'kpiSub', objectId: kpiSub.id }))
                    )
                 )
              ),
              ...kpiAreas.flatMap((kpiArea) =>
                 (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                    kpi.kpiSubs.flatMap((kpiSub: KpiSub) =>
                       kpiSub.kpiContents.flatMap((kpiContent: KpiContent) =>
                          (kpiContent?.kpiScopes ?? []).length === 0
                             ? [
                                  {
                                     id: -1,
                                     createdAt: formatISO(new Date()),
                                     updateAt: formatISO(new Date()),
                                     reason: '',
                                     isMaterial: true,
                                     businessImpact: undefined,
                                     stakeholderImpact: undefined,
                                     from: periodContext?.from,
                                     to: periodContext?.to,
                                     organisationId: activeOrganisation.id,
                                     kind: 'kpiContent',
                                     objectId: kpiContent.id,
                                  },
                               ]
                             : ((kpiContent?.kpiScopes ?? []).map((kpiScope: KpiScope) => ({
                                  ...kpiScope,
                                  kind: 'kpiContent',
                                  objectId: kpiContent.id,
                               })) as Partial<KpiScope>[])
                       )
                    )
                 )
              ),
              ...kpiAreas.flatMap((kpiArea) =>
                 (kpiArea?.kpis ?? []).flatMap((kpi: KPI) =>
                    kpi.kpiSubs.flatMap((kpiSub: KpiSub) =>
                       kpiSub.kpiContents.flatMap((kpiContent: KpiContent) =>
                          kpiContent.kpiFields
                             .filter(({ fieldGroupId }: KpiField) => !fieldGroupId)
                             .flatMap((kpiField: KpiField) =>
                                (kpiField?.kpiScopes ?? []).length === 0
                                   ? [
                                        {
                                           id: -1,
                                           createdAt: formatISO(new Date()),
                                           updateAt: formatISO(new Date()),
                                           reason: '',
                                           isMaterial: true,
                                           businessImpact: undefined,
                                           stakeholderImpact: undefined,
                                           from: periodContext?.from,
                                           to: periodContext?.to,
                                           organisationId: activeOrganisation.id,
                                           kind: 'kpiField',
                                           objectId: kpiField.id,
                                        } as Partial<KpiScope>,
                                     ]
                                   : (kpiField?.kpiScopes ?? []).map((kpiScope: KpiScope) => ({
                                        ...kpiScope,
                                        kind: 'kpiField',
                                        objectId: kpiField.id,
                                     }))
                             )
                       )
                    )
                 )
              ),
           ]
         : []
   ),
   distinctUntilChanged(isEqual)
);

export const loading$ = query.selectLoading();
